/**
 * Image Paste Resize Utility
 * Resizes an image pasted into a cell so its longer side is
 * MAX_CELL_IMAGE_SIZE, scaling the shorter side proportionally.
 */

export const MAX_CELL_IMAGE_SIZE = 120;
const JPEG_QUALITY = 0.8;
// Safety margin under the server's 100kb JSON body limit: ~90,000 base64
// chars is roughly 66KB of raw bytes, leaving headroom for the rest of the
// saved record.
const MAX_DATA_URL_LENGTH = 90000;

// e.g. 100 (h) x 200 (w) -> width capped to 120, height = 120 * (100/200) = 60
export const calculateResizedImageDimensions = (
  naturalWidth,
  naturalHeight,
  maxSize = MAX_CELL_IMAGE_SIZE
) => {
  const scale = maxSize / Math.max(naturalWidth, naturalHeight);
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale)
  };
};

const drawToDataUrl = (img, w, h, mimeType, quality = JPEG_QUALITY) => {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return mimeType === 'image/png'
    ? canvas.toDataURL('image/png')
    : canvas.toDataURL('image/jpeg', quality);
};

/**
 * Reads an image File, draws it onto an offscreen canvas at
 * calculateResizedImageDimensions, and re-encodes it so the returned data URL
 * actually contains the downscaled pixels (not just display width/height).
 * PNGs stay PNG (to keep transparency); everything else becomes JPEG. If the
 * result is still too large (e.g. a busy/complex image), falls back to a
 * smaller, more compressed JPEG so saving it never exceeds the server's
 * request body limit.
 */
export const resizeImageFile = (imageFile, maxSize = MAX_CELL_IMAGE_SIZE) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = calculateResizedImageDimensions(
          img.naturalWidth,
          img.naturalHeight,
          maxSize
        );

        let dataUrl = drawToDataUrl(img, width, height, imageFile.type);

        if (dataUrl.length > MAX_DATA_URL_LENGTH) {
          const fallbackWidth = Math.round(width * 0.5);
          const fallbackHeight = Math.round(height * 0.5);
          dataUrl = drawToDataUrl(img, fallbackWidth, fallbackHeight, 'image/jpeg', 0.6);
        }

        resolve({ dataUrl, width, height });
      };
      img.onerror = reject;
      img.src = event.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(imageFile);
  });
};

const getClipboardImageItem = e => {
  const items = e.clipboardData && e.clipboardData.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf('image') === 0) return items[i];
  }
  return null;
};

/**
 * Intercepts a paste event on a contentEditable cell. If the clipboard holds
 * an image, inserts it downscaled via resizeImageFile and returns true.
 * Otherwise leaves the event untouched (default text/html paste runs) and
 * returns false.
 */
export const handleImagePaste = (e, onImageInserted) => {
  const imageItem = getClipboardImageItem(e);
  if (!imageItem) return false;

  e.preventDefault();

  resizeImageFile(imageItem.getAsFile()).then(({ dataUrl, width, height }) => {
    document.execCommand(
      'insertHTML',
      false,
      `<img src="${dataUrl}" width="${width}" height="${height}">`
    );
    if (onImageInserted) onImageInserted();
  });

  return true;
};

/**
 * Intercepts a paste event on a plain <textarea>/<input> that holds raw HTML
 * source (the ListData/ListProData edit fields). If the clipboard holds an
 * image, downscales it via resizeImageFile and calls
 * insertTag(imgTagString, selectionStart, selectionEnd) so the caller can
 * splice it into its own controlled value at the cursor position. Otherwise
 * leaves the event untouched and returns false.
 */
export const handleTextareaImagePaste = (e, insertTag) => {
  const imageItem = getClipboardImageItem(e);
  if (!imageItem) return false;

  e.preventDefault();

  const { selectionStart, selectionEnd } = e.target;
  resizeImageFile(imageItem.getAsFile()).then(({ dataUrl, width, height }) => {
    insertTag(
      `<img src="${dataUrl}" width="${width}" height="${height}">`,
      selectionStart,
      selectionEnd
    );
  });

  return true;
};

/**
 * Finds the <img ...src=...> tag enclosing cursorPos in text, if any. The
 * base64 data URL these tags carry can run to tens of thousands of
 * characters - there's never a reason to select/edit only part of it, so
 * treating the whole tag as one selectable unit is what makes it possible
 * to delete in a single action instead of dragging across the whole blob.
 */
export const findImageTagRange = (text, cursorPos) => {
  const start = text.lastIndexOf('<', cursorPos);
  if (start === -1) return null;
  const end = text.indexOf('>', cursorPos);
  if (end === -1) return null;
  const tag = text.slice(start, end + 1);
  return /^<img\b[^>]*\bsrc=/i.test(tag) ? { start, end: end + 1 } : null;
};

/**
 * Double-click handler for a plain <textarea> holding raw HTML source. If
 * the click landed inside an <img ...> tag, selects the whole tag in place
 * of the browser's default word selection, so Delete/Backspace removes it
 * in one action.
 */
export const selectImageTagOnDoubleClick = e => {
  const el = e.target;
  const range = findImageTagRange(el.value, el.selectionStart);
  if (range) {
    el.setSelectionRange(range.start, range.end);
  }
};
